import { TrendingUp } from "lucide-react";
import type { TendenciaMensalGamificacao } from "@/features/home/gamificationApi";
import { AppEmptyState, AppPanel, AppSectionHeader } from "@/shared/components/AppUi";

export function PersonalEvolutionTrend({ data, simpleLanguage = false }: { data: TendenciaMensalGamificacao[]; simpleLanguage?: boolean }) {
  const points = (data ?? []).map((item, index) => ({ ...item, index })).filter(item => item.registros > 0);
  const width = 320;
  const height = 150;
  const padX = 24;
  const padTop = 18;
  const padBottom = 30;
  const coords = points.map((point, index) => ({
    ...point,
    x: points.length <= 1 ? width / 2 : padX + index * ((width - padX * 2) / (points.length - 1)),
    y: padTop + (100 - Math.max(0, Math.min(100, point.frequenciaPercentual))) / 100 * (height - padTop - padBottom)
  }));
  const path = coords.map((point, index) => `${index === 0 ? "M" : "L"}${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(" ");
  const current = points.at(-1);
  const previous = points.at(-2);
  const delta = current && previous ? current.frequenciaPercentual - previous.frequenciaPercentual : null;

  return <AppPanel className="p-4">
    <AppSectionHeader
      kicker="Tendência"
      title={simpleLanguage ? "Ritmo nos últimos meses" : "Seu ritmo ao longo do tempo"}
      detail={simpleLanguage ? "Frequência registrada mês a mês." : "A comparação principal é com seu próprio histórico."}
      action={<TrendingUp className="h-5 w-5 text-teal-light"/>}
    />
    {coords.length === 0 ? <AppEmptyState className="mt-4" title="Ainda não há meses suficientes para mostrar uma tendência."/> : <>
      {current && <div className="mt-4 flex items-end justify-between gap-3">
        <div><strong className="font-display text-3xl text-white">{current.frequenciaPercentual.toFixed(0)}%</strong><span className="ml-2 text-[10px] text-white/35">{current.periodo}</span></div>
        {delta !== null && <span className={`text-[10px] font-bold ${delta > 0 ? "text-teal-light" : delta < 0 ? "text-amber-100" : "text-white/40"}`}>{delta > 0 ? "+" : ""}{delta.toFixed(0)} p.p.</span>}
      </div>}
      <div className="mt-3 overflow-hidden rounded-xl bg-black/15 p-2">
        <svg viewBox={`0 0 ${width} ${height}`} className="h-auto w-full" role="img" aria-label="Tendência mensal de frequência">
          {[0,50,100].map(value => { const y = padTop + (100-value)/100*(height-padTop-padBottom); return <g key={value}><line x1={padX} y1={y} x2={width-padX} y2={y} stroke="currentColor" className="text-white/8"/><text x="2" y={y+3} fill="currentColor" className="text-white/28" fontSize="8">{value}%</text></g>; })}
          {path && <path d={path} fill="none" stroke="currentColor" className="text-teal-light" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>}
          {coords.map(point => <g key={point.periodo}><circle cx={point.x} cy={point.y} r="4" fill="currentColor" className="text-teal-light"/><text x={point.x} y={height-8} textAnchor="middle" fill="currentColor" className="text-white/35" fontSize="8">{shortPeriod(point.periodo)}</text></g>)}
        </svg>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">{data.slice(-3).map(item => <div key={item.periodo} className="rounded-xl border border-white/7 bg-black/10 p-2.5 text-center"><strong className="block text-xs text-white/70">{item.treinos}</strong><span className="mt-0.5 block text-[9px] text-white/32">treinos · {shortPeriod(item.periodo)}</span></div>)}</div>
    </>}
  </AppPanel>;
}

function shortPeriod(value: string) {
  const [month, year] = value.split("/");
  return month && year ? `${month}/${year.slice(-2)}` : value;
}
